// The Implementation Guide Framework — the product, rendered and edited here.
//
// Mirrors workers/3rdeyefeel-api/src/guideTypes.ts. Ten canonical components,
// each a titled section with an intro and a uniform list of items. Keep this in
// sync with the Worker: it generates exactly this shape into
// ai_analyses.guide_components, and this app renders + edits it.

import {
  Boxes,
  ListChecks,
  Compass,
  MapPin,
  Microscope,
  BarChart3,
  Sparkles,
  GitBranch,
  Hash,
  TerminalSquare,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { GuideComponent, GuideComponentKey } from '../types';

export const GUIDE_COMPONENT_KEYS: GuideComponentKey[] = [
  'resourcesNeeded',
  'stepsToBuild',
  'navigation',
  'placesToVisit',
  'research',
  'analysis',
  'prompts',
  'decisions',
  'keywords',
  'environment',
];

export interface GuideComponentMeta {
  label: string;
  blurb: string;
  itemLabel: string;
  icon: LucideIcon;
  /** tailwind accent token used by this app's theme (see index.css) */
  accent: string;
  checklist?: boolean;
  linky?: boolean;
}

export const GUIDE_COMPONENT_META: Record<GuideComponentKey, GuideComponentMeta> = {
  resourcesNeeded: {
    label: 'Resources Needed',
    blurb: 'Tools, libraries, services, accounts, hardware and docs required to build this.',
    itemLabel: 'Resource',
    icon: Boxes,
    accent: '#22d3ee',
    linky: true,
  },
  stepsToBuild: {
    label: 'Steps to Build',
    blurb: 'The ordered, actionable build sequence — check off each step to track progress.',
    itemLabel: 'Step',
    icon: ListChecks,
    accent: '#a78bfa',
    checklist: true,
  },
  navigation: {
    label: 'Navigation',
    blurb: 'How to move through the project: the map, the phases, where each piece lives.',
    itemLabel: 'Waypoint',
    icon: Compass,
    accent: '#34d399',
    linky: true,
  },
  placesToVisit: {
    label: 'Places to Visit',
    blurb: 'Websites, docs, dashboards and communities to open while building this.',
    itemLabel: 'Place',
    icon: MapPin,
    accent: '#f472b6',
    linky: true,
  },
  research: {
    label: 'Research',
    blurb: 'Topics to investigate, questions to answer, and the key findings behind them.',
    itemLabel: 'Finding',
    icon: Microscope,
    accent: '#60a5fa',
    linky: true,
  },
  analysis: {
    label: 'Analysis',
    blurb: 'Trade-offs, risks, and reasoning that shape how you approach the work.',
    itemLabel: 'Insight',
    icon: BarChart3,
    accent: '#fbbf24',
  },
  prompts: {
    label: 'Prompts',
    blurb: 'Ready-to-use AI prompts for the parts of this task an assistant can accelerate.',
    itemLabel: 'Prompt',
    icon: Sparkles,
    accent: '#c084fc',
  },
  decisions: {
    label: 'Decisions',
    blurb: 'The choices that must be made, the options, and the recommended call.',
    itemLabel: 'Decision',
    icon: GitBranch,
    accent: '#fb923c',
  },
  keywords: {
    label: 'Keywords',
    blurb: 'The important terms, concepts and jargon — with plain-language definitions.',
    itemLabel: 'Term',
    icon: Hash,
    accent: '#2dd4bf',
  },
  environment: {
    label: 'Environment',
    blurb: 'Environment setup, configuration and variables needed to run this.',
    itemLabel: 'Setting',
    icon: TerminalSquare,
    accent: '#818cf8',
  },
};

/** An empty, fully-scaffolded guide (all 10 components, no items) — what a
 * free/self-authored task starts from so the user can fill it in. */
export function emptyGuideComponents(): GuideComponent[] {
  return GUIDE_COMPONENT_KEYS.map((key) => ({ key, intro: '', items: [] }));
}

/** Merge stored components onto the canonical 10, in canonical order, so the UI
 * always shows every component even if a stored guide is partial or reordered. */
export function normalizeGuideComponents(stored: GuideComponent[] | undefined | null): GuideComponent[] {
  const byKey = new Map((stored ?? []).map((c) => [c.key, c]));
  return GUIDE_COMPONENT_KEYS.map(
    (key) => byKey.get(key) ?? { key, intro: '', items: [] }
  );
}

/** Progress = fraction of "Steps to Build" checked off. */
export function guideProgress(components: GuideComponent[]): { done: number; total: number; pct: number } {
  const steps = components.find((c) => c.key === 'stepsToBuild')?.items ?? [];
  const total = steps.length;
  const done = steps.filter((s) => s.done).length;
  return { done, total, pct: total === 0 ? 0 : Math.round((done / total) * 100) };
}
